/**
 * AI Recruit - Admin Users Page
 * List, search, filter, and manage users (admin-only)
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { usersApi } from '@/services/api'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { usePermission } from '@/hooks/usePermission'
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserCircleIcon,
  PencilSquareIcon,
  KeyIcon,
  LockOpenIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline'

interface User {
  id: string
  email: string
  username: string
  first_name: string
  last_name: string
  full_name: string
  role: string
  role_id?: string | null
  is_active: boolean
  phone?: string
}

interface AvailableRole {
  id: string
  name: string
  legacy_key: string | null
  description: string | null
  is_system: boolean
}

type StatusFilter = 'all' | 'active' | 'inactive'

const UsersPage: React.FC = () => {
  const queryClient = useQueryClient()
  // Added 2026-08-23 as part of splitting the old bundled `users.manage`
  // into 5 independent capabilities — this page previously had zero
  // button-level gating of its own (access was all-or-nothing at the
  // /admin/users route level), so this is new wiring, not a rename of an
  // existing check. No `users.delete` check exists here because there is no
  // delete button in this page's UI at all (usersApi.delete() is defined
  // but never called).
  const { can } = usePermission()
  const canCreate = can('users.create')
  const canEdit = can('users.edit')
  // Split 2026-08-25 from the single `users.resetPassword` capability into
  // two — the reset-to-default (key icon) and set-custom-password (lock
  // icon) actions are now independently grantable (see backend split notes
  // in users.py).
  const canResetPasswordDefault = can('users.resetPasswordDefault')
  const canSetCustomPassword = can('users.setCustomPassword')
  // Added 2026-08-24 (frontend/backend gate mismatch fix) — the actual
  // list-users call this page makes is gated on `users.viewAll` on the
  // backend, which was missing from both this page's own check and the
  // /admin/users route gate in App.tsx. Without it, a role holding only
  // e.g. `users.create` (one of the *other* 5 capabilities that route
  // gate already accepted) could reach this page and then get a 403
  // loading the list. Guarding the fetch itself, not just the route,
  // since a role could reach this page via any one of the 6 OR'd
  // capabilities without holding this specific one.
  const canViewAll = can('users.viewAll')
  // Gate mismatch fix (Priority 4 audit) — creating a user with the
  // Manager role has always required users.createManager on top of the
  // outer users.create gate (create_user's target_is_manager sub-check,
  // users.py), but the Create form's role picker offered every role
  // unconditionally. Only gates the Create form — there's no equivalent
  // backend sub-check on editing an existing user's role.
  const canCreateManager = can('users.createManager')
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Every assignable role — built-in and custom. Was previously a
  // hardcoded 6-entry array here, which meant a role created via Roles &
  // Permissions could never show up anywhere on this page (filter,
  // create, or edit) — fixed by reading the real list from the backend.
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([])

  useEffect(() => {
    usersApi.getAvailableRoles()
      .then((res) => setAvailableRoles(res.data.available_roles || []))
      .catch(() => toast.error('Failed to load roles'))
  }, [])

  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>('')
  const [roleFilter, setRoleFilter] = useState<string>('') // holds a role id, or '' for All Roles
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalUsers, setTotalUsers] = useState(0)

  // Create modal states
  const [formOpen, setFormOpen] = useState<boolean>(false)
  const [creating, setCreating] = useState<boolean>(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Edit modal states
  const [editOpen, setEditOpen] = useState<boolean>(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [updating, setUpdating] = useState<boolean>(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Set password modal states
  const [setPasswordOpen, setSetPasswordOpen] = useState<boolean>(false)
  const [setPasswordUser, setSetPasswordUser] = useState<User | null>(null)
  const [setPasswordValue, setSetPasswordValue] = useState<string>('')
  const [setPasswordConfirm, setSetPasswordConfirm] = useState<string>('')
  const [setPasswordFieldError, setSetPasswordFieldError] = useState<string | null>(null)
  const [setConfirmFieldError, setSetConfirmFieldError] = useState<string | null>(null)
  const [setPasswordLoading, setSetPasswordLoading] = useState<boolean>(false)
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)

  const validateSetPassword = (pwd: string): string | null => {
    if (!pwd) return null
    if (pwd.length < 8) return 'Password must be at least 8 characters'
    if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter'
    if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number'
    if (!/[^A-Za-z0-9]/.test(pwd)) return 'Password must contain at least one special character'
    return null
  }

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    first_name: '',
    last_name: '',
    role_id: '',
    phone: '',
    is_active: true,
    send_invitation: true,
  })

  const [editFormData, setEditFormData] = useState({
    email: '',
    username: '',
    first_name: '',
    last_name: '',
    role_id: '',
    phone: '',
    is_active: true,
  })

  const totalPages = Math.ceil(totalUsers / pageSize)
  const hasNext = currentPage < totalPages

  const fetchUsers = useCallback(async () => {
    if (!canViewAll) {
      setLoading(false)
      setError("You don't have permission to view the user list.")
      return
    }
    try {
      setLoading(true)
      const params: { search?: string; role_id?: string; is_active?: boolean; page?: number; page_size?: number } = {
        page: currentPage,
        page_size: pageSize,
      }

      if (activeSearchTerm.trim()) {
        params.search = activeSearchTerm.trim()
      }
      if (roleFilter) {
        params.role_id = roleFilter
      }
      if (statusFilter === 'active') {
        params.is_active = true
      } else if (statusFilter === 'inactive') {
        params.is_active = false
      }

      const res = await usersApi.list(params)
      setUsers(res.data.users || [])
      setTotalUsers(res.data.total || 0)
      setError(null)
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [activeSearchTerm, roleFilter, statusFilter, currentPage, pageSize, canViewAll])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Handle search execution
  const handleSearch = useCallback(() => {
    setActiveSearchTerm(searchTerm.trim())
    setCurrentPage(1)
  }, [searchTerm])

  // Handle Enter key press
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }, [handleSearch])

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('')
    setActiveSearchTerm('')
    setCurrentPage(1)
  }, [])

  // Handle filter changes
  const handleFilterChange = useCallback((filterType: string, value: string) => {
    setCurrentPage(1)
    if (filterType === 'role') {
      setRoleFilter(value)
    } else if (filterType === 'status') {
      setStatusFilter(value as StatusFilter)
    }
  }, [])

  const resetCreateForm = useCallback(() => {
    setFormData({
      email: '', username: '', first_name: '', last_name: '', role_id: '', phone: '', is_active: true, send_invitation: true,
    })
    setFormError(null)
  }, [])

  const handleCloseCreateForm = useCallback(() => {
    setFormOpen(false)
    resetCreateForm()
  }, [resetCreateForm])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.role_id) {
      setFormError('Please select a role')
      return
    }
    try {
      setCreating(true)
      setFormError(null)
      await usersApi.create(formData)
      setFormOpen(false)
      resetCreateForm()
      await fetchUsers()
      queryClient.invalidateQueries({ queryKey: ['org-users'] })
      // A new user changes their role's user_count on the Roles &
      // Permissions page — that page's query has no other reason to
      // refetch until its own 5-minute staleTime elapses otherwise.
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] })
    } catch (e: any) {
      setFormError(e.response?.data?.detail || 'Failed to create user')
    } finally {
      setCreating(false)
    }
  }

  const handleEditClick = (user: User) => {
    setEditingUser(user)
    // Falls back to matching the legacy role string if role_id is missing
    // — a pre-existing user whose role_id was never backfilled shouldn't
    // just show a blank/unselected role in the edit form.
    const resolvedRoleId = user.role_id || availableRoles.find((r) => r.legacy_key === user.role)?.id || ''
    setEditFormData({
      email: user.email,
      username: user.username,
      first_name: user.first_name,
      last_name: user.last_name,
      role_id: resolvedRoleId,
      phone: user.phone || '',
      is_active: user.is_active,
    })
    setEditError(null)
    setEditOpen(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return

    try {
      setUpdating(true)
      setEditError(null)
      // An empty role_id means it couldn't be resolved (see handleEditClick)
      // — omit it rather than send an invalid value; every other field
      // still saves normally.
      const { role_id, ...rest } = editFormData
      await usersApi.update(editingUser.id, role_id ? editFormData : rest)
      setEditOpen(false)
      setEditingUser(null)
      await fetchUsers()
      queryClient.invalidateQueries({ queryKey: ['org-users'] })
      // A role change here moves this user between two roles' user_counts
      // on the Roles & Permissions page - same reasoning as handleCreate.
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] })
    } catch (e: any) {
      setEditError(e.response?.data?.detail || 'Failed to update user')
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-2">
            Manage users and their access
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setFormOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <UserCircleIcon className="w-5 h-5" />
            <span>New User</span>
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>
      )}

      {/* Search Interface */}
      <div className="space-y-4">
        <div className="flex items-center">
          <h3 className="text-lg font-medium text-gray-900">Search Users</h3>
        </div>

        {/* Search Input */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or username..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                className="px-4 py-2 rounded-lg font-medium flex items-center space-x-2 min-w-[100px] justify-center bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              >
                <MagnifyingGlassIcon className="w-4 h-4" />
                <span>Search</span>
              </button>

              {/* Clear Button */}
              {(activeSearchTerm || searchTerm) && (
                <button
                  onClick={handleClearSearch}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Pagination Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 rounded-lg shadow">
        {/* Filters */}
        <div className="flex items-center space-x-4">
          {/* Role Filter */}
          <div className="flex items-center space-x-2">
            <FunnelIcon className="w-4 h-4 text-gray-400" />
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[160px]"
              value={roleFilter}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <option value="">All Roles</option>
              {availableRoles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[120px]"
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Show:</span>
            <select
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[60px]"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setCurrentPage(1)
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">per page</span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
              title="Previous page"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>

            <span className="text-sm text-gray-700 px-2">
              Page {currentPage} of {totalPages || 1}
            </span>

            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!hasNext}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors"
              title="Next page"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Users ({totalUsers})
          </h3>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <LoadingSpinner />
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <UserCircleIcon className="w-12 h-12 text-gray-400 mx-auto" />
            <p className="mt-4 text-gray-600">
              {activeSearchTerm || roleFilter || statusFilter !== 'all'
                ? 'No users found matching your filters.'
                : 'No users found.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {users.map((user) => (
              <div key={user.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <UserCircleIcon className="w-10 h-10 text-gray-400" />
                      <div>
                        <div className="flex items-center space-x-2 flex-wrap">
                          <h4 className="text-sm font-medium text-gray-900">
                            {user.full_name}
                          </h4>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-sm text-gray-500">{user.email}</span>
                          <span className="text-sm text-gray-400">{user.username}</span>
                        </div>
                        <div className="mt-1">
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium capitalize">
                            {user.role.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {canEdit && (
                      <button
                        className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                        onClick={() => handleEditClick(user)}
                        title="Edit user"
                      >
                        <PencilSquareIcon className="w-5 h-5" />
                      </button>
                    )}
                    {canResetPasswordDefault && (
                      <button
                        className="p-2 rounded-lg text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-all"
                        onClick={async () => {
                          if (!confirm('Reset password to default and require change on next login?')) return
                          try {
                            await usersApi.resetPassword(user.id)
                            toast.success('Password reset. User must change it on next login.')
                          } catch (e: any) {
                            toast.error(e.response?.data?.detail || 'Failed to reset password')
                          }
                        }}
                        title="Reset password to default"
                      >
                        <KeyIcon className="w-5 h-5" />
                      </button>
                    )}
                    {canSetCustomPassword && (
                      <button
                        className="p-2 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-all"
                        onClick={() => {
                          setSetPasswordUser(user)
                          setSetPasswordValue('')
                          setSetPasswordConfirm('')
                          setSetPasswordFieldError(null)
                          setSetConfirmFieldError(null)
                          setShowNewPassword(false)
                          setShowConfirmPassword(false)
                          setSetPasswordOpen(true)
                        }}
                        title="Set custom password"
                      >
                        <LockOpenIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Pagination */}
      {totalUsers > 0 && (
        <div className="flex justify-center items-center space-x-4 bg-white p-4 rounded-lg shadow">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            <span>Previous</span>
          </button>

          <div className="flex items-center space-x-2">
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => {
              let startPage = Math.max(1, currentPage - 2)
              const endPage = Math.min(totalPages || 1, startPage + 4)

              if (endPage - startPage < 4) {
                startPage = Math.max(1, endPage - 4)
              }

              const pageNum = startPage + i
              if (pageNum > (totalPages || 1)) return null

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
          </div>

          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={!hasNext}
            className="flex items-center space-x-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span>Next</span>
            <ChevronRightIcon className="w-4 h-4" />
          </button>

          <div className="text-sm text-gray-600 ml-4">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Create User</h2>
              <button className="text-gray-500 hover:text-gray-700" onClick={handleCloseCreateForm}>✕</button>
            </div>
            <form onSubmit={handleCreate} className="px-6 py-4 space-y-4">
              {formError && (
                <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{formError}</div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input type="email" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username <span className="text-red-500">*</span></label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={formData.role_id} onChange={(e) => setFormData({ ...formData, role_id: e.target.value })} required>
                  <option value="" disabled>Select a role…</option>
                  {availableRoles
                    .filter((role) => canCreateManager || role.legacy_key !== 'manager')
                    .map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="flex items-center space-x-6">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="create-status"
                      checked={formData.is_active === true}
                      onChange={() => setFormData({ ...formData, is_active: true })}
                      className="form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="create-status"
                      checked={formData.is_active === false}
                      onChange={() => setFormData({ ...formData, is_active: false })}
                      className="form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
                {!formData.is_active && (
                  <p className="mt-2 text-xs text-orange-600">User will not be able to login</p>
                )}
              </div>
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button type="button" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" onClick={handleCloseCreateForm}>Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50" disabled={creating}>{creating ? 'Creating...' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editOpen && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Edit User</h2>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => { setEditOpen(false); setEditingUser(null); }}>✕</button>
            </div>
            <form onSubmit={handleUpdate} className="px-6 py-4 space-y-4">
              {editError && (
                <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{editError}</div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={editFormData.first_name} onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={editFormData.last_name} onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })} required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input type="email" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={editFormData.email} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username <span className="text-red-500">*</span></label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={editFormData.username} onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={editFormData.phone} onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={editFormData.role_id} onChange={(e) => setEditFormData({ ...editFormData, role_id: e.target.value })} required>
                  <option value="" disabled>Select a role…</option>
                  {availableRoles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="flex items-center space-x-6">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="edit-status"
                      checked={editFormData.is_active === true}
                      onChange={() => setEditFormData({ ...editFormData, is_active: true })}
                      className="form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="edit-status"
                      checked={editFormData.is_active === false}
                      onChange={() => setEditFormData({ ...editFormData, is_active: false })}
                      className="form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
                {!editFormData.is_active && (
                  <p className="mt-2 text-xs text-orange-600">User will not be able to login</p>
                )}
              </div>
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button type="button" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" onClick={() => { setEditOpen(false); setEditingUser(null); }}>Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50" disabled={updating}>{updating ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set Custom Password Modal */}
      {setPasswordOpen && setPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Set Password</h2>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => { setSetPasswordOpen(false); setSetPasswordUser(null) }}>✕</button>
            </div>
            <form
              className="p-6 space-y-4"
              onSubmit={async (e) => {
                e.preventDefault()
                setSetPasswordFieldError(null)
                setSetConfirmFieldError(null)
                const pwdError = validateSetPassword(setPasswordValue)
                if (pwdError) { setSetPasswordFieldError(pwdError); return }
                if (setPasswordValue !== setPasswordConfirm) {
                  setSetConfirmFieldError('Passwords do not match')
                  return
                }
                setSetPasswordLoading(true)
                try {
                  await usersApi.setPassword(setPasswordUser.id, setPasswordValue)
                  setSetPasswordOpen(false)
                  setSetPasswordUser(null)
                  toast.success(`Password updated successfully for ${setPasswordUser.full_name || setPasswordUser.email}.`)
                } catch (err: any) {
                  setSetPasswordFieldError(err.response?.data?.detail || 'Failed to set password')
                } finally {
                  setSetPasswordLoading(false)
                }
              }}
            >
              <p className="text-sm text-gray-600">
                Setting a new password for <span className="font-medium">{setPasswordUser.full_name || setPasswordUser.email}</span>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={setPasswordValue}
                    onChange={(e) => {
                      setSetPasswordValue(e.target.value)
                      setSetPasswordFieldError(validateSetPassword(e.target.value))
                      if (setPasswordConfirm && e.target.value === setPasswordConfirm) {
                        setSetConfirmFieldError(null)
                      }
                    }}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${setPasswordFieldError && setPasswordValue ? 'border-red-400' : 'border-gray-300'}`}
                    placeholder="Enter new password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {setPasswordFieldError && setPasswordValue && (
                  <p className="text-xs text-red-600 mt-1">{setPasswordFieldError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={setPasswordConfirm}
                    onChange={(e) => {
                      setSetPasswordConfirm(e.target.value)
                      if (setPasswordValue && e.target.value && e.target.value !== setPasswordValue) {
                        setSetConfirmFieldError('Passwords do not match')
                      } else {
                        setSetConfirmFieldError(null)
                      }
                    }}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${setConfirmFieldError ? 'border-red-400' : 'border-gray-300'}`}
                    placeholder="Confirm new password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {setConfirmFieldError && (
                  <p className="text-xs text-red-600 mt-1">{setConfirmFieldError}</p>
                )}
              </div>
              <div className="flex items-center justify-end space-x-3 pt-2 border-t border-gray-200">
                <button type="button" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" onClick={() => { setSetPasswordOpen(false); setSetPasswordUser(null) }}>Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50" disabled={setPasswordLoading}>{setPasswordLoading ? 'Saving...' : 'Set Password'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default UsersPage
